import { Address, Borrower, PriorName } from '../misc';
import { OfferCounterInfo, OfferState, OfferType } from '../offer';

export enum PoolStatus {
  DRAFT = 'draft',
  SYSTEM_PENDING = 'system_pending',
  AWAITING_INVESTORS = 'awaiting_investors',
  OFFER_RECEIVED = 'offer_received',
  IN_ESCROW = 'in_escrow',
  CLOSED = 'closed',
  CANCELED = 'canceled',
  FUNDING_REJECTED = 'funding_rejected',
  REJECTED = 'rejected',
  FUNDED = 'funded',
}

export enum ClientPoolStatus {
  COUNTER_OFFER_RECEIVED = 'counter_offer_received',
  ESCROW_DOCUMENTATION_COMPLETE = 'escrow_documentation_complete',
  INVESTOR_COUNTER_OFFER_ACCEPTED = 'investor_counter_offer_accepted',
  BORROWER_OFFER_ACCEPTED = 'borrower_offer_accepted',
}

export enum PoolType {
  EQUITY = 'equity',
  REFINANCE = 'refinance',
}

export enum LoanType {
  INTEREST_ONLY = 'interest-only',
  AMORTIZING = 'amortizing',
}

export enum PoolSortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export enum PoolSortBy {
  CREATED_AT = 'created_at',
}

export enum PropertyOccupancyStatus {
  PRIMARY_RESIDENCE = 'primary-residence',
  VACANT = 'vacant',
  TENANT_OCCUPIED = 'tenant-occupied',
  OWNER_OCCUPIED = 'owner-occupied',
}

export type PoolApplicant = {
  first_name: string;
  middle_name: string | null;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string | null;
};

export type PropertyAddress = Address & {
  is_same_as_mailing: boolean;
};

export type PropertyDetails = {
  occupancy_status: PropertyOccupancyStatus;
  percent_owned: string;
  property_value: string;
  mortgage_balance: string;
};

export type CoOwner = {
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  ownership_percentage: string | null;
  is_primary: boolean;
};

export type ExistingLoan = {
  original_amount: string;
  remaining_balance: string;
};

export type PropertyLink = {
  url: string;
};

export type PropertyOwnership = {
  co_owners: CoOwner[];
};

export type Property = {
  address: PropertyAddress;
  details: PropertyDetails;
  ownership: PropertyOwnership | null;
  existing_loans: ExistingLoan[] | null;
  links: PropertyLink[] | null;
};

export type LoanTerms = {
  amount: string;
  roi_rate: string;
  loan_type: LoanType;
  term: string;
  term_months: number;
};

export type PoolDocument = {
  key: string;
  name: string | null;
  title: string | null;
  doc_type: string;
  url: string;
  uploaded_at: string | null;
  uploaded_by_id: number;
};

export type PoolOffer = {
  id: number;
  investor_id: number;
  pool_id: number;
  type: OfferType;
  state: OfferState;
  created_at: string | null;
  updated_at: string | null;
  counter_info: OfferCounterInfo | null;
};

// Pool response types

export type PoolListItem = {
  id: number;
  pool_type: PoolType;
  status: PoolStatus;
  borrower: Borrower;
  created_at: string; // ISO string

  loan_terms: LoanTerms;
  offers: PoolOffer[] | null;

  // Property Location info
  location?: {
    city: string;
    state: string;
  };
  property: Property | null;
  is_favorite: boolean;
};

export type PoolDetail = {
  id: number;
  pool_type: PoolType;
  status: PoolStatus;
  borrower: Borrower;
  created_at: string; // ISO string
  updated_at: string; // ISO string
  note: string;

  applicant: PoolApplicant;
  prior_names?: PriorName[] | null;
  mailing_address?: Address;
  primary_address?: Address;
  property: Property;
  loan_terms: LoanTerms;
  documents: PoolDocument[];
  offers: PoolOffer[] | null;
  is_favorite: boolean;
};

export type PoolFilters = {
  status?: PoolStatus[];
  min_amount?: number;
  max_amount?: number;
  min_roi?: number;
  max_roi?: number;
  sort_order?: PoolSortOrder;
  sort_by?: PoolSortBy;
  pool_type?: PoolType;
  pool_program?: LoanType;
  min_property_value?: number;
  max_property_value?: number;
  favorites_only?: boolean;
};

export type FilterState = {
  minAmount: string;
  maxAmount: string;
  minROI: string;
  maxROI: string;
  sortOrder: PoolSortOrder;
  poolType: string;
  poolProgram: string;
  minPropertyValue: string;
  maxPropertyValue: string;
};

export const defaultFilterState: FilterState = {
  minAmount: '',
  maxAmount: '',
  minROI: '',
  maxROI: '',
  sortOrder: PoolSortOrder.DESC,
  poolType: '',
  poolProgram: '',
  minPropertyValue: '',
  maxPropertyValue: '',
};

export const toFilterState = (filters: PoolFilters): FilterState => ({
  minAmount: filters.min_amount?.toString() ?? '',
  maxAmount: filters.max_amount?.toString() ?? '',
  minROI: filters.min_roi?.toString() ?? '',
  maxROI: filters.max_roi?.toString() ?? '',
  sortOrder: filters.sort_order ?? PoolSortOrder.DESC,
  poolType: filters.pool_type ?? '',
  poolProgram: filters.pool_program ?? '',
  minPropertyValue: filters.min_property_value?.toString() ?? '',
  maxPropertyValue: filters.max_property_value?.toString() ?? '',
});

export type PoolOverviewData = {
  total_borrowed: number | null;
  funded_pools: number | null;
  pools_in_process: number | null;
  total_invested: number | null;
  invested_in_escrow: number | null;
  pending_pools: number | null;
  invested_offers: number | null;
  offers_or_counter_offers: number | null;
};
